// @ts-check
/** @jsx jsx */
import { gql } from '@apollo/client';
import { FormattedText, P, Text } from '@bottlebooks/gatsby-theme-base/src';
import { Trans } from '@lingui/macro';
import { graphql } from 'gatsby';
import React from 'react';
import { jsx } from 'theme-ui';
import Section from '../Section/Section';

export default function WineVineyard({ wine, ...rest }) {
  if (!wine) return null;
  if (
    !wine.vineyardInformation &&
    !wine.vineyard &&
    !wine.pruning &&
    !wine.training &&
    !wine.vineDensity &&
    !wine.yield &&
    !wine.vineyardAge &&
    !wine.vineyardElevation
  ) {
    return null;
  }
  // If only the vineyard name is set:  Display vineyard name in the content, as large text.
  // If the vineyardInformation is set, display it in the content
  return (
    <Section
      title={
        <React.Fragment>
          <Trans>Vineyard</Trans>
          {wine.vineyard && wine.vineyardInformation && (
            <React.Fragment>: {wine.vineyard}</React.Fragment>
          )}
        </React.Fragment>
      }
      {...rest}
    >
      {wine.vineyardInformation ? (
        <FormattedText variant="large" text={wine.vineyardInformation} />
      ) : (
        // If there's no vineyard description, we display the vineyard as a title.
        <P variant="title">{wine.vineyard}</P>
      )}
      <VineyardValue title={<Trans>Pruning</Trans>}>
        {wine.pruning}
      </VineyardValue>
      <VineyardValue title={<Trans>Training</Trans>}>
        {wine.training}
      </VineyardValue>
      <SoilTypes wine={wine} />
      <VineyardValue title={<Trans>Vine density</Trans>}>
        {wine.vineDensity}
      </VineyardValue>
      {wine.yield && (
        <VineyardValue title={<Trans>Yield</Trans>}>
          {wine.yield} hl/ha
        </VineyardValue>
      )}
      {wine.age && (
        <VineyardValue title={<Trans comment="The vineyard's age.">Age</Trans>}>
          {wine.age}
        </VineyardValue>
      )}
      {wine.elevation && (
        <VineyardValue
          title={<Trans comment="The vineyard's elevation.">Elevation</Trans>}
        >
          {wine.elevation}
        </VineyardValue>
      )}
    </Section>
  );
}

function SoilTypes({ wine, ...rest }) {
  if (!wine.soilTypes?.filter(({ soilType }) => Boolean(soilType)).join('')) {
    return null;
  }
  return (
    <VineyardValue title={<Trans>Soil</Trans>} {...rest}>
      {wine.soilTypes
        .map(
          ({ soilType, percentage }) =>
            `${percentage ? `${percentage}% ` : ''}${soilType}`
        )
        .join(', ')}
    </VineyardValue>
  );
}

function VineyardValue({ title, value = undefined, children }) {
  // Only if the raw value is null or empty we have supplied it.
  if (value === null || value === '') return null;
  if (!children) return null;
  return (
    <Text variant="large">
      {title}: {children}
    </Text>
  );
}

export const fragment = graphql`
  fragment bb_WineVineyard on Bottlebooks_Wine {
    vineyard
    vineyardInformation
    pruning
    training
    vineDensity
    yield
    vineyardAge
    vineyardElevation
    soilType
    soilTypes {
      soilType
      percentage
    }
  }
`;

WineVineyard.fragment = gql`
  fragment WineVineyard on Wine {
    vineyard
    vineyardInformation
    pruning
    training
    vineDensity
    yield
    vineyardAge
    vineyardElevation
    soilType
    soilTypes {
      soilType
      percentage
    }
  }
`;
